// src/utils/billPlan.js
// D-291 (Stabilization P3d-1) - the vendor's PURCHASE BILL for one goods
// receipt, worked out in ONE place (pure, no Firebase).
//
// WHY THIS FILE EXISTS
// Goods and bill are two events (Ravi, 21 Oct): the GRN puts the goods in
// stock; the vendor's bill (proforma or tax invoice) is entered later and
// THAT creates what we owe the vendor. This file turns a receipt + what was
// typed from the vendor's bill into the bill Zeno saves:
//   - item lines: one per received line, pre-filled from the PO line (rate
//     before GST after the line's discount, GST %, HSN); the billed qty is
//     the VENDOR's figure (up to 3 decimals) - the stock qty never changes
//   - charge lines (packing, freight on the bill ...): in what we owe and in
//     the GST credit, never in the stock value (Q2)
//   - CGST / SGST / IGST, round off and total: ALWAYS computeBill() - never
//     typed and never taken from the screen
//   - an Unregistered vendor charges no GST: every line must be GST 0
// The bill screen calls planBill() on every keystroke; purchaseBillService
// (P3d-3) calls it again INSIDE its transaction, with the receipt / PO /
// vendor / company it read there, and saves nothing when ok is false.
//
// Pure -> covered by src/utils/billPlan.test.js (Jest).

import {
  computeBill, roundHalfUp, toPaise, fromPaise, checkHsn, SAME_STATE, OTHER_STATE, MAX_GST_PERCENT, MAX_ROUND_OFF,
  supplyTypeOf, vendorGstinOf, vendorGstStatus, companyClaimsItc, normaliseGstin, validateGstin,
} from "./gstBill";
import { stockRateFromPOLine, isCustomPOLine } from "./grnPlan";

export const BILL_TYPES = ["tax_invoice", "proforma"];
export const MAX_INVOICE_NO_LENGTH = 30;
// A bill rate this far from the GRN (stock) rate is shown as a warning.
export const RATE_WARN_FRACTION = 0.005;

const num = (v) => (v === "" || v === null || v === undefined ? NaN : Number(v));
const text = (v) => String(v ?? "").trim();
// A real calendar date "YYYY-MM-DD" (2026-02-30 is not).
const isDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
};

/**
 * The vendor's invoice number as a duplicate-check key: upper case, letters
 * and digits only ("inv / 23-24 / 007" -> "INV2324007"). The service keeps
 * one purchase_bill_numbers doc per company + vendor + key, created in the
 * bill's own transaction, so the same invoice can never be entered twice.
 * "" -> no usable number.
 */
export const invoiceKey = (invoiceNo) => text(invoiceNo).toUpperCase().replace(/[^A-Z0-9]/g, "");

// The receipt lines a bill is for: received something, not cancelled.
const billableReceiptLines = (receiptItems) =>
  (Array.isArray(receiptItems) ? receiptItems : [])
    .map((it, idx) => ({ it, idx }))
    .filter(({ it }) => it && !it._cancelled && Number(it.received_qty || 0) > 0);

// A receipt line's id on the bill: the PO line_id; an extra item (not on the
// PO) or an old line without one gets a stable id from its position.
export const receiptLineId = (it, idx) => text(it?.line_id) || `rcpt_${idx}`;

/**
 * The bill type and the vendor's invoice no / date - ONE check, used by
 * planBill() (bill entry) and by purchaseBillService.upgradeProformaBill()
 * (proforma -> tax invoice), so both refuse exactly the same things.
 * A tax invoice needs an invoice no (with at least one letter or digit) and
 * a real date that is not after `today` ("YYYY-MM-DD").
 * @returns {{ errors: string[], bill_type: string|null, invoice_no: string,
 *             invoice_key: string, invoice_date: string }}
 */
export function checkInvoiceFields({ billType, invoiceNo, invoiceDate, today } = {}) {
  const errors = [];
  const type = BILL_TYPES.includes(billType) ? billType : null;
  if (!type) errors.push("Choose the bill type: tax invoice or proforma");
  const invNo = text(invoiceNo);
  const invKey = invoiceKey(invNo);
  const invDate = text(invoiceDate);
  if (invNo.length > MAX_INVOICE_NO_LENGTH) errors.push(`Invoice no can have at most ${MAX_INVOICE_NO_LENGTH} characters`);
  if (type === "tax_invoice") {
    if (!invKey) errors.push("Enter the vendor's invoice no (as printed on the tax invoice)");
    if (!invDate) errors.push("Enter the invoice date");
  }
  if (invDate) {
    if (!isDay(invDate)) errors.push("Invoice date is not a valid date");
    else if (isDay(text(today)) && invDate > text(today)) errors.push("Invoice date cannot be in the future");
  }
  return { errors, bill_type: type, invoice_no: invNo, invoice_key: invKey, invoice_date: invDate };
}

/**
 * The bill lines the screen starts with (item c). Billed qty = received qty
 * (the vendor's figure is typed over it); rate before GST = the PO line's
 * rate after its discount - empty for a "rate not agreed" line (typed from
 * the bill); GST % and HSN from the PO line.
 */
export function initialBillLines(receiptItems) {
  return billableReceiptLines(receiptItems).map(({ it, idx }) => {
    const rate = stockRateFromPOLine(it, { itcClaimed: true });
    const gst = num(it.gst_percent);
    return {
      line_id:      receiptLineId(it, idx),
      item_id:      text(it.item_id),
      item_name:    text(it.item_name),
      unit:         text(it.unit),
      billed_unit:  text(it.unit),
      received_qty: Number(it.received_qty),
      billed_qty:   String(Number(it.received_qty)),
      base_rate:    rate > 0 ? String(rate) : "",
      gst_percent:  Number.isFinite(gst) ? String(gst) : "",
      hsn_code:     text(it.hsn_code),
    };
  });
}

/**
 * @param {Object} a
 * @param {Array}  a.receiptItems   the receipt's items[] as saved
 * @param {Array}  a.lines          bill item lines [{ line_id, billed_qty, base_rate, gst_percent, hsn_code }]
 * @param {Array}  [a.charges]      [{ description, amount (before GST), gst_percent, hsn_code }]
 * @param {string} a.billType       "tax_invoice" | "proforma"
 * @param {string} [a.invoiceNo]    vendor's invoice no - required for a tax invoice
 * @param {string} [a.invoiceDate]  "YYYY-MM-DD" - required for a tax invoice
 * @param {string} a.today          "YYYY-MM-DD" - the invoice date cannot be after it
 * @param {string|null} a.supplyType  "intra" | "inter" (from the GSTINs, or chosen once)
 * @param {boolean} a.vendorUnregistered  every line must be GST 0
 * @param {boolean} a.itcClaimed    the company claims GST input credit
 * @param {boolean} [a.vendorGstValid] the vendor's GSTIN is valid (P3d-4c; default true).
 *   false -> no input credit on this bill: GST goes into the stock cost.
 * @param {Object} [a.stockFactorByLine] { [line_id]: purchase -> stock unit factor } (default 1)
 * @param {number} [a.roundOff]     typed round off (empty -> from the printed total
 *   when within +-1.00 of the total before round off, else to the nearest rupee - D-317)
 * @param {number} [a.printedTotal] "Total as printed on the bill"
 * @returns {Object} { ok, errors[], warnings[], bill_type, invoice_no, invoice_key,
 *   invoice_date, supply_type, lines[], charges[], items_taxable, charges_taxable,
 *   taxable_total, cgst, sgst, igst, tax_total, round_off, bill_total,
 *   printed_total, printed_diff, itc_eligible, itc_amount, stock_value, gst_in_stock_cost }
 */
export function planBill({
  receiptItems, lines, charges = [], billType, invoiceNo, invoiceDate, today,
  supplyType, vendorUnregistered = false, itcClaimed = false, vendorGstValid = true,
  stockFactorByLine = {}, roundOff, printedTotal,
} = {}) {
  const errors = [];
  const warnings = [];

  // ── bill type + vendor invoice ──
  const inv = checkInvoiceFields({ billType, invoiceNo, invoiceDate, today });
  inv.errors.forEach((e) => errors.push(e));
  const type = inv.bill_type;
  const invNo = inv.invoice_no;
  const invKey = inv.invoice_key;
  const invDate = inv.invoice_date;

  // ── supply: same state / other state / unregistered (no GST) ──
  const unregistered = vendorUnregistered === true;
  // P3d-4c: credit needs the company's claim AND a valid vendor GSTIN.
  const creditPossible = itcClaimed === true && !unregistered && vendorGstValid !== false;
  const supply = unregistered ? "unregistered"
    : supplyType === SAME_STATE || supplyType === OTHER_STATE ? supplyType : null;

  // ── item lines: every billable receipt line exactly once, nothing else ──
  const receipt = billableReceiptLines(receiptItems);
  const byId = new Map(receipt.map(({ it, idx }) => [receiptLineId(it, idx), it]));
  const typed = Array.isArray(lines) ? lines : [];
  const seen = new Set();
  typed.forEach((l, i) => {
    const id = text(l?.line_id);
    if (!byId.has(id)) errors.push(`Bill line ${i + 1} is not a line of this receipt`);
    else if (seen.has(id)) errors.push(`"${byId.get(id).item_name || id}" is on the bill twice`);
    seen.add(id);
  });
  for (const [id, it] of byId) {
    if (!seen.has(id)) errors.push(`"${it.item_name || id}" is missing on the bill`);
  }
  if (receipt.length === 0) errors.push("Nothing was received on this receipt - there is nothing to bill");

  const checkGst = (label, gstRaw) => {
    const gst = num(gstRaw);
    if (!Number.isFinite(gst)) errors.push(`${label}: GST % is required (type 0 if the bill has no GST)`);
    else if (gst < 0 || gst > MAX_GST_PERCENT) errors.push(`${label}: GST % must be between 0 and ${MAX_GST_PERCENT}`);
    else if (unregistered && gst !== 0) errors.push(`${label}: this vendor is unregistered - GST must be 0`);
  };
  const checkHsnOf = (label, code) => {
    const h = checkHsn(code);
    if (!h.ok) errors.push(`${label}: ${h.reason}`);
    return h.hsn;
  };

  const itemLines = typed.filter((l) => byId.has(text(l?.line_id))).map((l) => {
    const it = byId.get(text(l.line_id));
    const label = `"${text(it.item_name) || text(l.line_id)}"`;
    checkGst(label, l.gst_percent);
    if (!(num(l.base_rate) > 0)) errors.push(`${label}: enter the rate before GST from the bill`);
    return { l, it, label, hsn: checkHsnOf(label, l.hsn_code) };
  });

  // ── charge lines (packing, freight on the bill ...) ──
  const chargeList = (Array.isArray(charges) ? charges : []).filter((c) =>
    text(c?.description) || text(c?.amount) || text(c?.gst_percent));
  const chargeRows = chargeList.map((c, i) => {
    const label = text(c.description) ? `"${text(c.description)}"` : `Charge ${i + 1}`;
    if (!text(c.description)) errors.push(`${label}: write what the charge is for`);
    if (!(num(c.amount) > 0)) errors.push(`${label}: amount before GST must be more than 0`);
    checkGst(label, c.gst_percent);
    return { c, i, label, hsn: checkHsnOf(label, c.hsn_code) };
  });

  // ── the bill maths - computeBill, never the screen ──
  const billLines = [
    ...itemLines.map(({ l }) => ({ line_id: `item:${text(l.line_id)}`, billed_qty: l.billed_qty, base_rate: l.base_rate, gst_percent: unregistered ? 0 : l.gst_percent })),
    ...chargeRows.map(({ c, i }) => ({ line_id: `charge:${i}`, billed_qty: 1, base_rate: c.amount, gst_percent: unregistered ? 0 : c.gst_percent })),
  ];
  const billInput = {
    lines: billLines,
    // Unregistered: every line is 0 %, so the arithmetic is the same either way.
    supplyType: unregistered ? OTHER_STATE : supply,
    printedTotal,
  };
  let bill = computeBill({ ...billInput, roundOff });

  // D-317 (R12): the vendor's PRINTED total decides the round off. Within
  // +-1.00 of our total before round off -> round off = printed - before, so
  // the bill (and the payable) equals what the vendor printed. A typed round
  // off always wins; more than 1.00 away -> to the nearest rupee + a warning.
  const typedRoundOff = roundOff !== undefined && roundOff !== null && roundOff !== "";
  const printedNum = num(printedTotal);
  let roundOffSource = typedRoundOff ? "typed" : "auto";
  if (!typedRoundOff && Number.isFinite(printedNum) && printedNum >= 0 && bill.bill_total !== null) {
    const beforeP = toPaise(bill.bill_total) - toPaise(bill.round_off);
    const diffP = toPaise(printedNum) - beforeP;
    if (Math.abs(diffP) <= MAX_ROUND_OFF * 100) {
      bill = computeBill({ ...billInput, roundOff: fromPaise(diffP) });
      roundOffSource = "printed";
    } else if (!bill.warnings.some((w) => w.includes("differs from the printed total"))) {
      warnings.push(
        `Printed total ${fromPaise(toPaise(printedNum)).toFixed(2)} differs from the total before round off ` +
          `${fromPaise(beforeP).toFixed(2)} by ${(Math.abs(diffP) / 100).toFixed(2)} (more than ${MAX_ROUND_OFF}.00) - ` +
          "round off not taken from it; check the lines"
      );
    }
  }
  // computeBill names lines "Line n" - give the item / charge name instead.
  // Rate and GST % were already checked above (with clearer words), so only
  // its other line checks (qty, decimals, size) are passed on.
  const nameOfLine = (n) => (n < itemLines.length ? itemLines[n].label : chargeRows[n - itemLines.length]?.label);
  const ALREADY_CHECKED = ["rate before GST is missing", "GST % must be between"];
  bill.errors.forEach((e) => {
    const m = /^Line (\d+): (.*)$/.exec(e);
    if (!m) { if (e !== "Add at least one line") errors.push(e); return; }
    if (ALREADY_CHECKED.some((t) => m[2].startsWith(t))) return;
    const n = Number(m[1]) - 1;
    const words = n >= itemLines.length ? m[2].replace("rate before GST", "amount") : m[2];
    errors.push(`${nameOfLine(n)}: ${words}`);
  });
  bill.warnings.forEach((w) => {
    const m = /^Line (\d+): (.*)$/.exec(w);
    warnings.push(m ? `${nameOfLine(Number(m[1]) - 1)}: ${m[2]}` : w);
  });

  const taxableOf = (id) => bill.lines.find((x) => x.line_id === id)?.taxable ?? null;

  // ── item lines out: taxable, stock qty, cost per stock unit ──
  let itemsTaxableP = 0;
  let stockValueP = 0;
  const outLines = itemLines.map(({ l, it, label, hsn }) => {
    const id = text(l.line_id);
    const taxable = taxableOf(`item:${id}`);
    const gst = unregistered ? 0 : num(l.gst_percent);
    const factor = Number(stockFactorByLine?.[id]) > 0 ? Number(stockFactorByLine[id]) : 1;
    const stockQty = Number(it.received_qty) * factor;
    let lineCostP = null;
    if (taxable !== null && Number.isFinite(gst)) {
      itemsTaxableP += toPaise(taxable);
      // Cost = taxable; + this line's GST when no input credit can be taken.
      const gstP = Math.round((toPaise(taxable) * Math.round(gst * 100)) / 10000);
      lineCostP = toPaise(taxable) + (creditPossible ? 0 : gstP);
      stockValueP += isCustomPOLine(it) ? 0 : lineCostP;
    }
    const costPerStockUnit = lineCostP !== null && stockQty > 0 ? roundHalfUp(fromPaise(lineCostP) / stockQty, 2) : null;

    // Bill qty / rate far from what was received - shown, never blocks.
    const billedQty = num(l.billed_qty);
    if (Number.isFinite(billedQty) && billedQty !== Number(it.received_qty)) {
      warnings.push(`${label}: billed qty ${billedQty} differs from received qty ${Number(it.received_qty)} - check with the vendor`);
    }
    const grnRate = Number(it.rate || 0);
    if (costPerStockUnit !== null && grnRate > 0 && factor === 1 &&
        Math.abs(costPerStockUnit - grnRate) / grnRate > RATE_WARN_FRACTION) {
      warnings.push(`${label}: cost per unit on the bill ${costPerStockUnit.toFixed(2)} differs from the GRN rate ${grnRate.toFixed(2)}`);
    }

    return {
      line_id:             id,
      item_id:             text(it.item_id),
      item_code:           text(it.item_code),
      item_name:           text(it.item_name),
      unit:                text(it.unit),
      // P3d-5a: the unit as PRINTED on the vendor's bill (e.g. Kgs for a
      // Bundle ordered) - shown and saved only, never used in the maths.
      billed_unit:         text(l.billed_unit) || text(it.unit),
      is_custom_item:      isCustomPOLine(it),
      hsn_code:            hsn,
      received_qty:        Number(it.received_qty),
      billed_qty:          Number.isFinite(billedQty) ? billedQty : null,
      base_rate:           Number.isFinite(num(l.base_rate)) ? num(l.base_rate) : null,
      gst_percent:         Number.isFinite(gst) ? gst : null,
      taxable,
      received_stock_qty:  stockQty,
      cost_per_stock_unit: costPerStockUnit,
    };
  });

  let chargesTaxableP = 0;
  const outCharges = chargeRows.map(({ c, i, hsn }) => {
    const taxable = taxableOf(`charge:${i}`);
    if (taxable !== null) chargesTaxableP += toPaise(taxable);
    const gst = unregistered ? 0 : num(c.gst_percent);
    return {
      description: text(c.description),
      hsn_code:    hsn,
      amount:      Number.isFinite(num(c.amount)) ? num(c.amount) : null,
      gst_percent: Number.isFinite(gst) ? gst : null,
      taxable,
    };
  });

  const ok = errors.length === 0;
  const totalsKnown = bill.bill_total !== null;
  // GST credit: only a TAX INVOICE from a vendor with a valid GSTIN, when the company claims ITC.
  const itcEligible = ok && creditPossible && type === "tax_invoice";
  if (itcClaimed === true && !unregistered && vendorGstValid === false && Number(bill.tax_total) > 0) {
    warnings.push("Vendor GSTIN missing or not valid - GST on this bill cannot be claimed as input credit. Fix it in Vendor Master.");
  }

  return {
    ok,
    errors,
    warnings,
    bill_type:       type,
    invoice_no:      invNo,
    invoice_key:     invKey,
    invoice_date:    invDate,
    supply_type:     supply,
    lines:           outLines,
    charges:         outCharges,
    items_taxable:   fromPaise(itemsTaxableP),
    charges_taxable: fromPaise(chargesTaxableP),
    taxable_total:   bill.taxable_total,
    cgst:            totalsKnown ? bill.cgst : null,
    sgst:            totalsKnown ? bill.sgst : null,
    igst:            totalsKnown ? bill.igst : null,
    tax_total:       totalsKnown ? bill.tax_total : null,
    round_off:       totalsKnown ? bill.round_off : null,
    // D-317: "printed" (taken from the printed total) | "typed" | "auto" -
    // screen text only, never saved.
    round_off_source: roundOffSource,
    bill_total:      totalsKnown ? bill.bill_total : null,
    printed_total:   bill.printed_total,
    printed_diff:    bill.printed_diff,
    itc_eligible:    itcEligible,
    itc_amount:      itcEligible ? bill.tax_total : 0,
    stock_value:     fromPaise(stockValueP),
    // true -> line GST is inside the stock cost (no credit possible when entered);
    // the proforma upgrade then gives no credit either (never count GST twice).
    gst_in_stock_cost: !creditPossible,
  };
}

// Company GSTIN missing / invalid: CGST / SGST / IGST cannot be decided.
export const COMPANY_GSTIN_ERROR =
  "Your company's GSTIN in Company Profile is missing or not valid - the owner must fix it before a bill with GST can be entered.";

/**
 * P3d-5a-6: the vendor's GSTIN AS PRINTED ON THE BILL decides the tax (a
 * registered vendor must print it; GST input credit follows the invoice).
 * bill.vendor_gstin_on_bill = what staff typed (pre-filled from Vendor Master):
 *   undefined -> Vendor Master's GSTIN (an "Unregistered" vendor -> none)
 *   ""        -> the bill shows no GSTIN: the vendor cannot charge GST
 * masterFix: the bill's GSTIN is valid and Vendor Master has none / an invalid
 *   one / "Unregistered" -> it may be saved to Vendor Master.
 * differs: valid, and Vendor Master has a DIFFERENT valid GSTIN -> only warned.
 */
export function billGstinOf(vendor, bill = {}) {
  const masterGstin = vendorGstinOf(vendor || {});
  const masterStatus = vendorGstStatus(vendor || {});
  const typed = bill.vendor_gstin_on_bill !== undefined && bill.vendor_gstin_on_bill !== null;
  const raw = typed ? bill.vendor_gstin_on_bill : (masterStatus === "unregistered" ? "" : masterGstin);
  const gstin = normaliseGstin(raw);
  const check = gstin ? validateGstin(gstin) : null;
  const valid = !!check?.ok;
  return {
    gstin,
    check,
    masterGstin,
    masterStatus,
    noGstin: !gstin,
    masterFix: valid && masterStatus !== "registered",
    differs: valid && masterStatus === "registered" && gstin !== masterGstin,
  };
}

/**
 * P3d-5a: the ONE place that turns a receipt + the company + the vendor +
 * what was typed from the vendor's bill into planBill()'s inputs. The bill
 * page calls it on every keystroke; purchaseBillService.createPurchaseBill
 * calls it inside its transaction with what it read there. So the total on
 * the screen is, by construction, the total that is saved.
 *   - P3d-5a-6: the vendor GSTIN PRINTED ON THE BILL (billGstinOf) decides
 *     CGST + SGST / IGST with the company's GSTIN - no screen question; no
 *     GSTIN on the bill -> GST 0; an invalid one -> refused; company GSTIN
 *     missing -> refused with the Company Profile message
 *   - GST input credit: company claims ITC AND the bill's GSTIN is valid
 *   - a proforma saves NO invoice no / date: the real number and date come
 *     with the tax invoice (upgrade), so the duplicate check always runs
 * @param {Object} a
 * @param {Array}  a.receiptItems  the receipt's items[] as saved
 * @param {Object} a.company       companies/{id} data
 * @param {Object} a.vendor        vendors/{id} data
 * @param {Object} a.bill          what was typed: { bill_type, vendor_invoice_no,
 *   vendor_invoice_date, vendor_gstin_on_bill, lines [{ line_id, billed_qty,
 *   billed_unit, base_rate, gst_percent, hsn_code }], charges, round_off,
 *   printed_total, stock_factor_by_line }
 * @param {string} a.today         "YYYY-MM-DD" (India)
 * @returns the planBill() result + { vendor_gstin, vendor_master_gstin, vendor_master_fix }
 */
export function planBillFor({ receiptItems, company, vendor, bill = {}, today } = {}) {
  const c = company || {};
  const g = billGstinOf(vendor, bill);
  const gstinOk = !!g.check?.ok;
  const companyOk = validateGstin(c.gst_number).ok;
  const isProforma = bill.bill_type === "proforma";
  const plan = planBill({
    receiptItems,
    lines:              bill.lines,
    charges:            bill.charges,
    billType:           bill.bill_type,
    invoiceNo:          isProforma ? "" : bill.vendor_invoice_no,
    invoiceDate:        isProforma ? "" : bill.vendor_invoice_date,
    today,
    supplyType:         gstinOk ? supplyTypeOf(c.gst_number, g.gstin) : null,
    vendorUnregistered: g.noGstin,
    itcClaimed:         companyClaimsItc(c),
    vendorGstValid:     gstinOk,
    stockFactorByLine:  bill.stock_factor_by_line || {},
    roundOff:           bill.round_off,
    printedTotal:       bill.printed_total,
  });

  // The same / other state question no longer exists - say what to fix instead.
  const SUPPLY_QUESTION = "Choose same state (CGST + SGST) or other state (IGST)";
  const errors = plan.errors.filter((e) => e !== SUPPLY_QUESTION);
  if (!g.noGstin && !gstinOk) errors.unshift(`GSTIN on the bill: ${g.check.reason}`);
  else if (!g.noGstin && !companyOk) errors.unshift(COMPANY_GSTIN_ERROR);

  const warnings = [...plan.warnings];
  if (g.differs) {
    warnings.unshift(`GSTIN on the bill ${g.gstin} differs from Vendor Master ${g.masterGstin} - check it is the same vendor (another branch / wrong vendor?). Vendor Master is not changed.`);
  }

  const ok = errors.length === 0;
  return {
    ...plan,
    ok,
    errors,
    warnings,
    itc_eligible:        ok && plan.itc_eligible,
    itc_amount:          ok && plan.itc_eligible ? plan.itc_amount : 0,
    vendor_gstin:        g.noGstin ? "" : g.gstin,
    vendor_master_gstin: g.masterGstin,
    vendor_master_fix:   g.masterFix,
  };
}